// Module: S04SuccessContinue
// Role: Template rendered when latestAttempt.verdict === 'accepted'.
//       Shows accepted banner + next checkpoint instruction + continue CTA.
// Uses: flow/atoms (Frame, MiniHeader, CtaBar, InstructionCard),
//       styles/tokens (P, S, T, G),
//       hooks/usePlatformBack (ADR-023 — no raw window.Telegram)
// Used by: FlowScreen (accepted-verdict dispatch case)
// Glossary: ai/glossary/flow_engine.md
//
// L-240: Inline interface for GetFlowResponse fields — do NOT import api/types.ts.

import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { Check } from '../atoms/Icons';
import { StepStrip } from '../StepStrip';
import { P, S, T } from '../../../styles/tokens';
import { usePlatformBack } from '../../../hooks/usePlatformBack';
import { flowStepLabel } from '../_labels';

const SUCCESS_SOFT_BG = '#EFF7E9';

// ─── Inline interfaces (L-240) ─────────────────────────────────────────────────
// Field names MUST match the backend exactly (flow.py _api_schemas.py).
// Do not import from api/flow-types.ts — use inline only.

interface S04Checkpoint {
  id: string;
  slug: string;
  kind: string;
  position: number;
  instructions_text: string | null;
  metadata: Record<string, unknown>;
}

interface S04Props {
  /** The checkpoint whose attempt was just accepted. */
  acceptedCheckpoint: S04Checkpoint;
  /** Total number of checkpoints in the flow (for StepStrip). */
  totalCheckpoints: number;
  /** The next checkpoint to execute after this one. May be null on the final step. */
  nextCheckpoint: S04Checkpoint | null;
  /** Called when the buyer taps "Продолжить". */
  onCta: () => void;
}

// ─── Accepted banner ────────────────────────────────────────────────────────────

function AcceptedBanner() {
  return (
    <div
      style={{
        marginTop: 14,
        padding: 14,
        background: SUCCESS_SOFT_BG,
        border: '1px solid rgba(45,138,78,0.30)',
        borderRadius: 14,
        display: 'flex',
        gap: 12,
        alignItems: 'center',
      }}
    >
      {/* Check circle */}
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 18,
          background: P.success,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: '#fff',
        }}
      >
        <Check size={18} />
      </div>

      <div style={{ flex: 1 }}>
        <div style={{ font: T.headline, color: P.success, fontWeight: 700 }}>
          Скрин принят!
        </div>
        <div
          style={{
            font: T.footnote,
            color: S.label,
            marginTop: 2,
            lineHeight: 1.45,
          }}
        >
          Отлично, переходи к следующему шагу.
        </div>
      </div>
    </div>
  );
}

// ─── Next step instruction block ────────────────────────────────────────────────

interface NextStepBlockProps {
  checkpoint: S04Checkpoint;
}

function NextStepBlock({ checkpoint }: NextStepBlockProps) {
  const steps = Array.isArray(checkpoint.metadata?.steps)
    ? (checkpoint.metadata.steps as string[])
    : undefined;

  return (
    <div style={{ marginTop: 20 }}>
      {checkpoint.instructions_text && (
        <div
          style={{
            font: T.title3,
            color: S.label,
            marginBottom: 12,
            lineHeight: 1.2,
          }}
        >
          {checkpoint.instructions_text}
        </div>
      )}
      {steps && steps.length > 0 && (
        <InstructionCard>
          <ul
            style={{
              margin: 0,
              padding: '0 0 0 18px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            {steps.map((step, i) => (
              <li
                key={i}
                style={{ font: T.subhead, color: S.label, lineHeight: 1.45 }}
              >
                {step}
              </li>
            ))}
          </ul>
        </InstructionCard>
      )}
    </div>
  );
}

// ─── Main template ──────────────────────────────────────────────────────────────

export function S04SuccessContinue({
  acceptedCheckpoint,
  totalCheckpoints,
  nextCheckpoint,
  onCta,
}: S04Props) {
  // ADR-023: use hook, never raw window.Telegram
  usePlatformBack();

  const headerTitle = nextCheckpoint
    ? `Шаг ${nextCheckpoint.position + 1} из ${totalCheckpoints}`
    : `Шаг ${acceptedCheckpoint.position + 1} из ${totalCheckpoints}`;

  const headerSub = nextCheckpoint
    ? flowStepLabel(nextCheckpoint.kind)
    : flowStepLabel(acceptedCheckpoint.kind);

  return (
    <Frame>
      <MiniHeader title={headerTitle} subtitle={headerSub} />

      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div style={{ paddingTop: 12 }}>
          <StepStrip
            current={acceptedCheckpoint.position + 1}
            total={totalCheckpoints}
            showCaption
          />
        </div>

        <AcceptedBanner />

        {nextCheckpoint ? (
          <NextStepBlock checkpoint={nextCheckpoint} />
        ) : (
          <div
            style={{
              marginTop: 20,
              font: T.bodyText,
              color: S.secondaryLabel,
              textAlign: 'center',
            }}
          >
            Все шаги выполнены!
          </div>
        )}
      </div>

      <WizardCtaBar label="Продолжить" onClick={onCta} />
    </Frame>
  );
}
